'use client';

import {
  SetStateAction, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import { useSWRConfig } from 'swr';
import { TailwindSpinner } from '@components/common';
import { useSwrJobListCardsInfinite } from '@hooks/useSwrApi/endpoints/job/useSwrJobListCardsInfinite';
import { JobCardList } from '@components/templates/job/Card/JobCardList';
import { buildSearchParams } from '@utils/data-fetching/searchParams';
import { useFiltersQueryParamsAppFolder } from '@hooks/filters/useFiltersQueryParamsAppFolder';
import { JobSearchFilters } from '@components/features/filters/JobSearchFilters';
import { JobSearchQueryParams } from '@type/filters';
import { useSetHeaderSearchContext } from '@components/contexts/HeaderSearchContext';
import { useMcSharedStateOptional } from '@components/contexts/McSharedStateContext';
import { CandidateLanding, CoachingLanding } from '@components/features/candidate-landing';
import { useJobSearchFiltersFromSidebar } from './useJobSearchFiltersFromSidebar';
import { BestieMessagesList } from './BestieMessagesList';
import { EmptyJobListFallback } from './EmptyJobListFallback';
import { MyBestieLanding } from '../../templates/my-bestie-landing';
import { useSidebarNavigation, type CandidateSearchFilters } from '../../contexts/SidebarNavigationContext';

interface JobListContentProps {
  locale: Locale;
  isAuthenticated?: boolean;
  /** SSR-fetched job data so SWR uses it as fallback and the backend receives auth headers on first load. */
  serverSideJobData?: ApiSchema<'CursorJobResult'>;
  /** SSR-fetched stories data for the coaching empty state. */
  initialStoriesData?: ApiSchema<'ResumeStoriesResult'>;
}

/**
 * @description Displays job list based on current sidebar selection.
 * - "New Job Search" → shows all/recommended jobs (no filters)
 * - Selected alert → shows jobs filtered by alert's saved search criteria
 */
export const JobListContent = (props: JobListContentProps) => {
  const {
    locale, isAuthenticated = false, serverSideJobData, initialStoriesData,
  } = props;
  const { t } = useTranslation('common');

  // Get filters from sidebar navigation
  const {
    filters, isNewSearch, isMyBestieLanding, isJobSearchForm,
  } = useJobSearchFiltersFromSidebar();
  const { currentView, navigateToCandidateSearch } = useSidebarNavigation();

  // Bestie chat stream lives in the MC shell (McSharedStateProvider) so it survives
  // navigation between views; the coaching view reads it from there.
  const stream = useMcSharedStateOptional()?.stream ?? null;

  // Filter drawer state — managed independently so the floating filter button can open it
  const { queryParams, setQueryParams } = useFiltersQueryParamsAppFolder<'cursor'>({
    paginationType: 'cursor',
    preserveNumericLastSegment: true,
  });

  // Marks the next setQueryParamsFromDrawer call as user-initiated. Set by SidebarJobSearchFiltersHub
  // before any user action (Apply, filter toggle, group/all delete). Auto-submits triggered by the
  // initial form populate (e.g. decomposed filters injected into drawerQueryParams below) leave
  // this false, so the URL is not touched until the user actually changes something.
  const userInitiatedSubmitRef = useRef(false);
  const markUserSubmit = useCallback(() => { userInitiatedSubmitRef.current = true; }, []);

  // Drawer-applied filters: location is a URL-path filter on MC (`/mc/job/list/location-...`),
  // not a query string filter. Extract it on both the form view AND the candidateSearch results
  // view; the remaining filters (careerLevels, domains, etc.) still flow to the query string via
  // the underlying setQueryParams. On the results view we preserve the existing keyword/salary
  // and decomposed-fields metadata when navigating, so toggling a location doesn't drop them.
  //
  // Gated on `userInitiatedSubmitRef` so the hub's auto-submit-on-populate doesn't rewrite the
  // URL with the display-only decomposed filters we inject into drawerQueryParams below.
  const setQueryParamsFromDrawer = useCallback(
    (next: SetStateAction<object>) => {
      if (!userInitiatedSubmitRef.current) return;
      userInitiatedSubmitRef.current = false;
      const isCandidateSearchView = typeof currentView === 'object' && 'candidateSearch' in currentView;
      if (!isJobSearchForm && !isCandidateSearchView) {
        setQueryParams(next);
        return;
      }
      const resolved = (typeof next === 'function' ? (next as (p: object) => object)(queryParams) : next) as Partial<JobSearchQueryParams>;
      const { location, ...rest } = resolved;
      const locations = Array.isArray(location) ? location.filter(Boolean) : undefined;
      const currentCandidate = isCandidateSearchView ? currentView.candidateSearch : undefined;
      navigateToCandidateSearch({
        ...(currentCandidate?.keyword && { keyword: currentCandidate.keyword }),
        ...(currentCandidate?.salary && { salary: currentCandidate.salary }),
        ...(currentCandidate?.originalKeyword && { originalKeyword: currentCandidate.originalKeyword }),
        ...(currentCandidate?.decomposedFilters && { decomposedFilters: currentCandidate.decomposedFilters }),
        ...(locations?.length && { locations }),
      });
      setQueryParams(rest);
    },
    [setQueryParams, isJobSearchForm, navigateToCandidateSearch, queryParams, currentView],
  );

  // Keys managed by SidebarNavigationContext — must be excluded from headerQueryParams
  // to prevent the header's useFiltersHub from feeding them back into setQueryParams,
  // which would cause useAppHistoryAndURL to push a stale URL and trigger an infinite loop.
  const headerQueryParams = useMemo(() => {
    const sidebarKeys = ['keyword', 'location', 'sort', 'careerLevels', 'domain', 'employmentType', 'spokenLanguages', 'remote'];
    const strip = (obj: Record<string, unknown>) => Object.fromEntries(
      Object.entries(obj).filter(([key]) => !sidebarKeys.includes(key)),
    );
    // Strip sidebar-managed keys from BOTH sources:
    // - filters: these come from the sidebar selection and shouldn't flow to the header
    // - queryParams: the header's useFiltersHub can inject e.g. keyword='' back into
    //   queryParams, which would leak into headerQueryParams and sustain the feedback loop
    return { ...strip((filters ?? {})), ...strip(queryParams as Record<string, unknown>) };
  }, [filters, queryParams]);
  // Pass a no-op setQueryParams to the header context. On MC pages, keyword is managed
  // by SidebarNavigationContext. Passing the real setQueryParams allows the header's
  // useFiltersHub to inject keyword='' back into queryParams, creating an infinite
  // re-render loop (useFiltersHub always returns a new object reference, which cascades
  // through the context back into setQueryParams).
  const headerSetQueryParamsNoop = useCallback(() => {}, []);
  useSetHeaderSearchContext({ queryParams: headerQueryParams, setQueryParams: headerSetQueryParamsNoop });

  // Merge sidebar-derived filters (alert/keyword) with drawer-applied URL params.
  // `filters` carries keyword/alert fields; `queryParams` carries domain, careerLevels, etc.
  // `queryParams` wins on overlap so explicit drawer selections always take effect,
  // except for keyword which is managed by the sidebar — strip it from queryParams
  // to prevent a stale keyword='' (injected by the header's useFiltersHub) from
  // overriding the sidebar's keyword.
  const urlParams = useMemo(() => {
    const { keyword: sidebarKeyword, ...drawerParams } = queryParams as Record<string, unknown>;
    void sidebarKeyword;
    const combined = { ...(filters ?? {}), ...drawerParams };
    return buildSearchParams(combined, '&', false);
  }, [filters, queryParams]);

  // Pagination state
  const [, setCurrentPage] = useState<string>('');

  useEffect(() => {
    setCurrentPage(''); // Reset to first page when search changes
  }, [urlParams]);

  // Only fetch jobs when the list view is actually visible — avoids an unfiltered
  // background fetch while on the landing/form views, which would flash as stale
  // data (keepPreviousData) the first time a user picks a search.
  const shouldFetchJobs = !isNewSearch && !isMyBestieLanding && !isJobSearchForm;

  // Fetch job cards with infinite scroll
  const swrResponse = useSwrJobListCardsInfinite({
    locale,
    limit: 24,
    urlParams,
    initialCursor: '',
    serverSideData: serverSideJobData,
    shouldFetch: shouldFetchJobs,
  });

  const {
    data: jobListData,
    size,
    isLoading,
  } = swrResponse;

  // Surface backend-decomposed filters to the drawer so the user sees what the keyword search
  // resolved to (e.g. typing "manager bucuresti" yields location=București + domain=Management).
  // Display-only: the URL stays as the user typed it. `userInitiatedSubmitRef` above gates the
  // auto-submit-on-populate so it doesn't rewrite the URL with the decomposed values.
  //
  // URL-path location (from `currentView.candidateSearch.locations`) wins over the response's
  // decomposed `locations` so explicit filters are not overwritten by re-decomposition. When
  // both are present, the canonical name from `decomposedFilters` is preferred over the URL
  // slug ("București" not "bucuresti"). Explicit query-string filters in `queryParams` always
  // win over decomposed ones for the same reason.
  const drawerQueryParams = useMemo(() => {
    const candidateSearch = typeof currentView === 'object' && 'candidateSearch' in currentView
      ? currentView.candidateSearch
      : undefined;
    const decomposed = candidateSearch?.decomposedFilters ?? jobListData?.[0]?.decomposedFilters;
    const pathLocations = candidateSearch?.locations?.length
      ? (candidateSearch.decomposedFilters?.locations ?? candidateSearch.locations)
      : decomposed?.locations;
    const qp = queryParams as Partial<JobSearchQueryParams>;
    const fromDecomposed: Partial<JobSearchQueryParams> = {};
    if (decomposed) {
      if (!qp.domain?.length && decomposed.domains?.length) fromDecomposed.domain = decomposed.domains;
      if (!qp.careerLevels?.length && decomposed.careerLevels?.length) fromDecomposed.careerLevels = decomposed.careerLevels;
      if (!qp.employmentTypes?.length && decomposed.employmentTypes?.length) fromDecomposed.employmentTypes = decomposed.employmentTypes;
      if (!qp.spokenLanguages?.length && decomposed.spokenLanguages?.length) fromDecomposed.spokenLanguages = decomposed.spokenLanguages;
      if (qp.remote == null && decomposed.remote) fromDecomposed.remote = 1;
      if (!qp.benefits?.length && decomposed.benefits?.length) fromDecomposed.benefits = decomposed.benefits.map(String);
    }
    return {
      ...qp,
      ...fromDecomposed,
      ...(pathLocations?.length && { location: pathLocations }),
    };
  }, [queryParams, currentView, jobListData]);

  const allItems = jobListData?.flatMap((p) => p?.items ?? []) ?? [];
  const isCandidateSearchView = typeof currentView === 'object' && 'candidateSearch' in currentView;

  const shouldRenderFallback = (
    isCandidateSearchView
    && !isLoading
    && !swrResponse.isValidating
    && allItems.length === 0
  );

  const fallbackCandidateSearch: CandidateSearchFilters | undefined = isCandidateSearchView
    ? (currentView).candidateSearch
    : undefined;
  // Match the priority used elsewhere in this file (drawerQueryParams): prefer the view-level
  // decomposedFilters (carried via the post-fetch redirect), fall back to the response.
  // Merge in explicit `locations`/`salary` from the candidateSearch view — when the user
  // entered them as separate inputs (not embedded in the keyword), the backend's
  // decomposedFilters won't include them and the relaxation logic in
  // EmptyJobListFallback wouldn't see anything to relax.
  const fallbackDecomposedFilters = useMemo(() => {
    const base = fallbackCandidateSearch?.decomposedFilters
      ?? jobListData?.[0]?.decomposedFilters;
    if (!fallbackCandidateSearch) return base;
    const locations = base?.locations?.length
      ? base.locations
      : fallbackCandidateSearch.locations;
    const minSalary = base?.minSalary ?? fallbackCandidateSearch.salary;
    return {
      ...base,
      ...(locations?.length && { locations }),
      ...(minSalary != null && { minSalary }),
    };
  }, [fallbackCandidateSearch, jobListData]);

  // When the backend returns a valid searchId, a new alert was created — refresh the sidebar
  const { mutate: globalMutate } = useSWRConfig();
  const lastRefreshedSearchId = useRef<number | null>(null);
  useEffect(() => {
    const firstGroup = jobListData?.length ? jobListData[0] : undefined;
    const rawSearchId = firstGroup?.searchId;
    if (rawSearchId == null) return;
    const parsedSearchId = parseInt(rawSearchId, 10);
    if (Number.isInteger(parsedSearchId) && parsedSearchId > 0 && parsedSearchId !== lastRefreshedSearchId.current) {
      lastRefreshedSearchId.current = parsedSearchId;
      // Revalidate without clearing the cache (passing undefined would cause a flash to empty data
      // which breaks the alertId → filters lookup and creates an infinite refetch loop).
      void globalMutate(
        (key: unknown) => Array.isArray(key) && typeof key[0] === 'string' && key[0].includes('/v1/user/job-search-alerts'),
      );
    }
  }, [jobListData, globalMutate]);

  const isCoachingActive = isNewSearch && (stream?.messages?.length ?? 0) > 0;
  const isCoachingEmpty = isNewSearch && !isCoachingActive;

  const mainContent = (() => {
    if (isMyBestieLanding) {
      return <MyBestieLanding locale={locale} isAuthenticated={isAuthenticated} />;
    }
    if (isCoachingActive) {
      return <BestieMessagesList stream={stream} locale={locale} />;
    }
    if (isCoachingEmpty) {
      return <CoachingLanding locale={locale} initialStoriesData={initialStoriesData} />;
    }
    if (isJobSearchForm) {
      return <CandidateLanding locale={locale} isAuthenticated={isAuthenticated} />;
    }
    return (
      <div className="container mx-auto px-4 py-6">
        {/* Loading state - initial load */}
        {isLoading && size === 1 && (
          <div className="flex items-center justify-center py-16">
            <TailwindSpinner className="size-8 text-primary" />
          </div>
        )}

        {/* Job cards list (also renders the existing empty-state UI when items are empty) */}
        {(!isLoading || size > 1) && (
          <>
            <JobCardList
              {...swrResponse}
              t={t}
              setCurrentPage={setCurrentPage}
              locale={locale}
              paginationType="cursor"
              forceModalOnMc
              useCurrentUrlForModal
            />
            {shouldRenderFallback && (
              <EmptyJobListFallback
                locale={locale}
                candidateSearch={fallbackCandidateSearch}
                decomposedFilters={fallbackDecomposedFilters}
              />
            )}
          </>
        )}
      </div>
    );
  })();

  return (
    <div className="flex min-h-full grow flex-col bg-surface-100">
      {mainContent}

      {/* Filter drawer — opened by the filter button in the prompt bar (rendered by the MC shell).
          drawerQueryParams = URL queryParams + backend-decomposed filters (location, domain,
          careerLevels, etc.) so the drawer reflects what the keyword search resolved to. Explicit
          URL values always win over decomposed ones. `onUserSubmit` lets the hub signal
          user-initiated submits so setQueryParamsFromDrawer only rewrites the URL when the user
          actually interacts — auto-submits from the initial populate stay display-only. */}
      <JobSearchFilters
        queryParams={drawerQueryParams}
        setQueryParams={setQueryParamsFromDrawer}
        onUserSubmit={markUserSubmit}
      />
    </div>
  );
};
